import { FC, useContext, useEffect, useState } from 'react';
import { keyBy } from 'lodash-es';
import { Spinner } from '@chakra-ui/react';

import { useApplicationForm } from '~/core/shared/api/middle-office/application/hooks/useApplicationForm';
import { useApplicationFormContext } from '~/core/shared/api/middle-office/application/hooks/useApplicationFormContext';
import { useApplicationFormSubmit } from '~/core/shared/api/middle-office/application/hooks/useApplicationFormSubmit';
import { EMPTY_OBJ } from '~/core/shared/constants';
import { useRequiredParams } from '~/core/shared/hooks';
import { FormContext } from '~/core/shared/types';
import { handleError, handleSuccess } from '~/core/shared/utils/admin';
import { TenantDocumentRouteProps } from '../../../config';
import { ApplicationUpdateForm } from '../form';
import { ApplicationContext } from '../Layout.component';

type UpdateTabProps = {
  updateTemplateName: string;
  refreshOnSave?: boolean;
  onlyDirtyFields?: boolean;
} & TenantDocumentRouteProps;

export const UpdateTab: FC<UpdateTabProps> = ({
  updateTemplateName,
  refreshOnSave,
  onlyDirtyFields,
}) => {
  const { definition } = useContext(ApplicationContext);
  const { uuid } = useRequiredParams(['uuid']);
  const { submitForm } = useApplicationFormSubmit(definition);
  const { form, refetch: refetchForm } = useApplicationForm(uuid, definition, {
    form: updateTemplateName,
  });
  const { formContext } = useApplicationFormContext(uuid, definition, {
    form: updateTemplateName,
  });
  const [formContexts, setFormContexts] =
    useState<Record<string, FormContext>>();
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    if (refreshOnSave) {
      refetchForm();
    }
  }, [refreshOnSave]);

  useEffect(() => {
    if (formContext) {
      setFormContexts(keyBy(formContext, (c) => c.name));
    }
  }, [formContext]);

  const [validationErrors, setValidationErrors] = useState<Record<string, any>>(
    {},
  );

  const onSubmit = async (formValues: any): Promise<void> => {
    setIsSaving(true);
    try {
      await submitForm({
        uuid,
        data: formValues,
        params: { form: updateTemplateName },
      });
      setValidationErrors(EMPTY_OBJ);
      handleSuccess('Application updated');
      if (refreshOnSave) {
        setTimeout(() => refetchForm(), 2000);
      }
    } catch (error: any) {
      if (error?.response?.status === 422) {
        const errors = error?.response?.data?.errors;
        if (errors) setValidationErrors(errors);
        handleError('Validation failed');
      } else {
        handleError(error?.message || 'An error occurred');
      }
    }
    setIsSaving(false);
  };

  if (form && formContexts) {
    return (
      <ApplicationUpdateForm
        form={form}
        formContexts={formContexts}
        isSaving={isSaving}
        onlyDirtyFields={onlyDirtyFields}
        onSubmit={onSubmit}
        validationErrors={validationErrors}
      />
    );
  }

  return <Spinner />;
};
